import { useHotkeys } from '@tanstack/react-hotkeys';

/**
 * Viewer-scoped hotkeys — single-key shortcuts for 3D viewer controls.
 * These are registered against a container ref so they only fire
 * when the viewer is focused (not when typing in inputs).
 */
export const useViewerHotkeys = ({
  onToggleMap,
  onTogglePointcloud,
  onToggleClipBox,
  onToggleOrthoCamera,
  onToggleThirdPerson,
  onToggleStreetView,
  onToggleMeasureDistance,
  onToggleMeasureArea,
  onScreenshot,
  hasPointclouds,
  hasMeasureableTargets,
  isFlying,
  isGoogleStreetViewEnabled,
  canUseClipBox,
}: {
  onToggleMap: () => void;
  onTogglePointcloud: () => void;
  onToggleClipBox: () => void;
  onToggleOrthoCamera: () => void;
  onToggleThirdPerson: () => void;
  onToggleStreetView: () => void;
  onToggleMeasureDistance: () => void;
  onToggleMeasureArea: () => void;
  onScreenshot: () => void;
  hasPointclouds: boolean;
  hasMeasureableTargets: boolean;
  isFlying: boolean;
  isGoogleStreetViewEnabled: boolean;
  // Subscription-tier gate. Hotkey is silently disabled when false; the toolbar
  // button surfaces the upgrade prompt instead — see `viewer.clipBox` in
  // app/src/lib/featureGates.ts.
  canUseClipBox: boolean;
}) => {
  useHotkeys([
    {
      hotkey: 'm',
      callback: onToggleMap,
      options: { ignoreInputs: true },
    },
    {
      hotkey: 'p',
      callback: onTogglePointcloud,
      options: { ignoreInputs: true, enabled: hasPointclouds },
    },
    {
      hotkey: 'c',
      callback: onToggleClipBox,
      options: { ignoreInputs: true, enabled: hasPointclouds && canUseClipBox },
    },
    {
      hotkey: 'o',
      callback: onToggleOrthoCamera,
      options: { ignoreInputs: true, enabled: isFlying },
    },
    {
      hotkey: 't',
      callback: onToggleThirdPerson,
      options: { ignoreInputs: true, enabled: isFlying },
    },
    {
      hotkey: 'g',
      callback: onToggleStreetView,
      options: { ignoreInputs: true, enabled: isGoogleStreetViewEnabled },
    },
    {
      hotkey: 'r',
      callback: onToggleMeasureDistance,
      options: { ignoreInputs: true, enabled: hasMeasureableTargets },
    },
    {
      hotkey: 'b',
      callback: onToggleMeasureArea,
      options: { ignoreInputs: true, enabled: hasMeasureableTargets },
    },
    {
      hotkey: 'i',
      callback: onScreenshot,
      options: { ignoreInputs: true },
    },
  ]);
};
